// CRM — `xsib-crm` moduli bilan haqiqiy kontrakt.
// Manba (taxmin emas, o'qilgan): `crm/ticket/**`, `crm/lead/**`,
// `crm/interaction/**` — `*Resource.java`, `*Query.java`, domen enum'lari.
//
// ⚠️ HAMMA MAYDONDA `.nullish()`, `.nullable()` EMAS.
// Backendning hamma servisida `default-property-inclusion: non_null`:
// `null` maydon JSON'ga UMUMAN chiqmaydi. Ya'ni maydon yo qiymat bilan
// keladi, yo BUTUNLAY yo'q — "null bilan keldi" holati bo'lmaydi.
// `nullable()` aynan o'sha bo'lmaydigan holatni kutadi va maydon
// yo'qligida SXEMA YIQILADI. Bu tuzoq mehmon portalida jonli tizimda
// ushlangan: kirish berilgan, ekranda esa xom Zod xatosi turgan.
//
// ⚠️ LID TELEFONI NIQOBLANGAN. `list`/`get` to'liq raqamni BERMAYDI
// (`LeadResource` javadoc'i) — buning uchun alohida `crm.lead.reveal`
// huquqi va alohida `POST /{id}/reveal` chaqiruvi bor, u SABAB so'raydi
// va audit hodisasiga tushadi. Shuning uchun UI da telefonni "shunchaki
// ko'rsatib qo'yish" mumkin emas: aks holda huquq modeli qog'ozda
// qolib, amalda aylanib o'tilgan bo'lardi.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { instantSchema, uuidSchema } from '@/shared/api/schemas'
import { showsClosed } from '@/shared/ui/table/closedFilter'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'

// --- Enum'lar (manba: `crm/*/domain/*.java`) ---------------------------------

export const TICKET_STATUSES = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'REOPENED', 'CLOSED'] as const
export type TicketStatus = (typeof TICKET_STATUSES)[number]

export const TICKET_CATEGORIES = ['CONNECTIVITY', 'BILLING', 'EQUIPMENT', 'BEHAVIOR', 'OTHER'] as const
export type TicketCategory = (typeof TICKET_CATEGORIES)[number]

export const TICKET_PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const
export type TicketPriority = (typeof TICKET_PRIORITIES)[number]

export const LEAD_STATUSES = ['NEW', 'CONTACTED', 'QUALIFIED', 'WON', 'LOST'] as const
export type LeadStatus = (typeof LEAD_STATUSES)[number]

export const LEAD_SOURCES = ['WEBSITE', 'REFERRAL', 'AD', 'WALK_IN', 'PHONE', 'AGENT', 'OTHER'] as const
export type LeadSource = (typeof LEAD_SOURCES)[number]

export const INTERACTION_CHANNELS = ['CALL', 'SMS', 'EMAIL', 'VISIT', 'CHAT', 'OTHER'] as const
export type InteractionChannel = (typeof INTERACTION_CHANNELS)[number]

export const INTERACTION_DIRECTIONS = ['INBOUND', 'OUTBOUND'] as const
export type InteractionDirection = (typeof INTERACTION_DIRECTIONS)[number]

export const INTERACTION_OUTCOMES = ['RESOLVED', 'FOLLOW_UP_NEEDED', 'NO_ANSWER', 'ESCALATED', 'OTHER'] as const
export type InteractionOutcome = (typeof INTERACTION_OUTCOMES)[number]

/**
 * Holat mashinasi — `TicketStatus.allowedTargets()` ning aksi.
 *
 * ⚠️ UI da FAQAT ruxsat etilgan o'tish tugmasi chiziladi. Ilgari boshqa
 * sahifalarda hammasi ko'rsatilardi va backend rad javobi bilan
 * tugardi, operator esa nima uchunligini bilmasdi
 * (`SubscriberActions` javadoc'idagi bir xil xato).
 *
 * <h2>⚠️ BU XARITA SERVER BILAN IKKI JOYDA MOS BO'LISHI SHART</h2>
 * Manba haqiqat IKKITA va ular bir xil:
 *   · `TicketStatus.allowedTargets()` (xsib-crm) — erta va tushunarli xato;
 *   · `crm.ticket_state_transition_guard()` trigger'i (V202608050200) —
 *     kod chetlab o'tilsa ham qoladi.
 *
 * ⚠️ O'LCHANGAN NUQSON (tester-2, BUG-039; Staging, 2026-08-16). Bu yerda
 * `OPEN` va `REOPENED` ga `RESOLVED` YOZILGAN edi, serverda esa ikkalasidan
 * ham `RESOLVED` ga yo'l YO'Q:
 *
 *   OPEN        -> IN_PROGRESS | CLOSED
 *   IN_PROGRESS -> RESOLVED    | CLOSED
 *   RESOLVED    -> REOPENED    | CLOSED
 *   REOPENED    -> IN_PROGRESS | CLOSED
 *
 * Natijada «Qayta ochilgan» qatorida «Hal qilindi» tugmasi CHIZILARDI va
 * bosilganda 409/`IllegalTransition` qaytardi. Ya'ni sahifaning o'z
 * javadoc'ida yozilgan «faqat ruxsat etilgan o'tish» va'dasi aynan shu
 * ikki holatda buzilardi. Tugmani ko'rsatib, keyin rad etish — panelning
 * boshqa joylarida allaqachon tuzatilgan xato.
 *
 * ⚠️ Xarita `tests/crm-defects.mjs` da `TicketStatus.java` MANBASIDAN
 * o'qib solishtiriladi — qo'lda yozilgan nusxa jimgina ajralib ketmasin.
 */
export const TICKET_NEXT: Record<TicketStatus, readonly TicketStatus[]> = {
  OPEN: ['IN_PROGRESS', 'CLOSED'],
  IN_PROGRESS: ['RESOLVED', 'CLOSED'],
  RESOLVED: ['REOPENED', 'CLOSED'],
  REOPENED: ['IN_PROGRESS', 'CLOSED'],
  CLOSED: [],
}

export const LEAD_NEXT: Record<LeadStatus, readonly LeadStatus[]> = {
  NEW: ['CONTACTED', 'LOST'],
  CONTACTED: ['QUALIFIED', 'LOST'],
  QUALIFIED: ['WON', 'LOST'],
  WON: [],
  LOST: [],
}

/**
 * Sahifalangan javob.
 *
 * ⚠️ Maydon nomi `rows`, `items` EMAS — staging'da tekshirildi
 * (`{"data":{"rows":[],"total":0,"page":0}}`). Nomni taxmin qilish
 * bo'sh jadval bilan tugardi va sababi ko'rinmasdi.
 */
function pageSchema<T extends z.ZodTypeAny>(row: T) {
  return z.object({
    rows: z.array(row),
    total: z.number(),
    page: z.number(),
    size: z.number().nullish(),
  })
}

export interface Page<T> {
  rows: T[]
  total: number
  page: number
}

/**
 * Bo'sh matn → `null`.
 *
 * ⚠️ Bo'sh satr YUBORILMAYDI: bazada `''` va `null` boshqa qiymat, va
 * "izoh yo'q" ni bo'sh satr bilan yozish keyin `notes IS NULL`
 * hisobotlarini jimgina buzardi.
 */
function blankToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed === undefined || trimmed === '' ? null : trimmed
}

/** Serverdan kelgan xom matn ro'yxatdagi qiymatmi — tip darajasida */
function oneOf<T extends string>(value: string, all: readonly T[]): T | null {
  return (all as readonly string[]).includes(value) ? (value as T) : null
}

// --- Tiket --------------------------------------------------------------------

const ticketSchema = z.object({
  id: uuidSchema,
  tenantId: uuidSchema.nullish(),
  subscriberId: uuidSchema.nullish(),
  subscriberLogin: z.string().nullish(),
  venueId: uuidSchema.nullish(),
  venueName: z.string().nullish(),
  category: z.string(),
  priority: z.string(),
  subject: z.string(),
  description: z.string().nullish(),
  status: z.string(),
  assignedTo: uuidSchema.nullish(),
  teamId: uuidSchema.nullish(),
  teamName: z.string().nullish(),
  assignedAt: instantSchema.nullish(),
  /** Mas'ulga WhatsApp xabari notify navbatiga qo'yilgan payt — telefonga yetgani emas */
  assigneeNotifiedAt: instantSchema.nullish(),
  receivedAt: instantSchema.nullish(),
  confirmedAt: instantSchema.nullish(),
  confirmedBy: uuidSchema.nullish(),
  openedBy: uuidSchema.nullish(),
  openedAt: instantSchema,
  /** SLA muddati — shu vaqtdan keyin tiket kechikkan hisoblanadi */
  dueAt: instantSchema.nullish(),
  /** Birinchi javob QACHON berilgani — SLA ning asosiy o'lchovi */
  firstResponseAt: instantSchema.nullish(),
  startedAt: instantSchema.nullish(),
  resolvedAt: instantSchema.nullish(),
  closedAt: instantSchema.nullish(),
  createdAt: instantSchema.nullish(),
  updatedAt: instantSchema.nullish(),
  /** Mas'ul belgilangan muddatda qabul qilmagan — server hisoblaydi, muddat serverdagi sozlamada */
  acknowledgeOverdue: z.boolean().nullish(),
  /** Biriktirishdan qabulgacha, soniya — server hisoblaydi */
  acknowledgeSeconds: z.number().int().nullish(),
  /** Yaratilgandan hal qilinguncha, soniya; faqat RESOLVED/CLOSED da */
  resolutionSeconds: z.number().int().nullish(),
})

export type Ticket = z.infer<typeof ticketSchema>

export interface TicketFilter {
  search?: string
  status?: TicketStatus
  /**
   * Yopilgan murojaatlar ro'yxatga kirsinmi.
   *
   * ⚠️ SERVERDA SUKUT — `true` (`TicketResource#list`), ya'ni parametr
   * yuborilmasa hammasi qaytadi. Yashirish qarori PANELNIKI va shuning
   * uchun bayroq HAR so'rovda ochiq yuboriladi.
   *
   * ⚠️ `status` aniq berilganda server buni E'TIBORSIZ qoldiradi
   * (`TicketQuery.excludesClosed`). Bu ataylab: usiz «faqat
   * yopilganlarni ko'rsat» DOIM bo'sh natija berardi.
   */
  includeClosed?: boolean
  category?: TicketCategory
  priority?: TicketPriority
  onlyOverdue?: boolean
  onlyAcknowledgeOverdue?: boolean
  page?: number
  size?: number
}

export async function fetchTickets(
  filter: TicketFilter = {},
  signal?: AbortSignal,
): Promise<Page<Ticket>> {
  const response = await api.get<unknown>('/api/crm/tickets', { params: filter, ...(signal ? { signal } : {}) })
  return pageSchema(ticketSchema).parse(response.data)
}

export async function fetchTicket(id: string): Promise<Ticket> {
  const response = await api.get<unknown>(`/api/crm/tickets/${id}`)
  return ticketSchema.parse(response.data)
}

export interface TicketDraft {
  subscriberId?: string | null
  venueId?: string | null
  category: TicketCategory
  priority: TicketPriority
  subject: string
  description?: string | null
  dueAt?: string | null
}

export async function createTicket(draft: TicketDraft): Promise<Ticket> {
  const response = await api.post<unknown>('/api/crm/tickets', draft)
  return ticketSchema.parse(response.data)
}

/**
 * Holat o'zgartirish — har o'tish O'Z endpointi bilan.
 *
 * ⚠️ Bitta "status" maydonini yangilash bilan almashtirilmadi: backend
 * har o'tishda boshqa ish bajaradi (`resolve` — `resolvedAt` qo'yadi,
 * `close` — `closedAt`, `reopen` — ikkalasini tozalaydi). Umumiy
 * yangilash ularni chetlab o'tardi.
 */
export async function ticketTransition(
  id: string,
  to: 'start' | 'resolve' | 'reopen' | 'close' | 'first-response' | TicketAcceptanceStep,
  body?: Record<string, unknown>,
): Promise<Ticket> {
  const response = await api.post<unknown>(`/api/crm/tickets/${id}/${to}`, body ?? {})
  return ticketSchema.parse(response.data)
}

/** Mas'ul va bo'lim birga almashtiriladi: `null` — biriktirilmagan. */
export async function assignTicket(
  id: string,
  assignedTo: string | null,
  teamId: string | null,
): Promise<Ticket> {
  const response = await api.post<unknown>(`/api/crm/tickets/${id}/assign`, { assignedTo, teamId })
  return ticketSchema.parse(response.data)
}

export const TICKET_STAGES = [
  'UNASSIGNED',
  'ASSIGNED',
  'RECEIVED',
  'CONFIRMED',
  'IN_PROGRESS',
  'RESOLVED',
  'CLOSED',
] as const
export type TicketStage = (typeof TICKET_STAGES)[number]

export type TicketAcceptanceStep = 'receive' | 'confirm'

type TicketProgress = Pick<Ticket, 'status' | 'assignedTo' | 'receivedAt' | 'confirmedAt'>

const awaitsAcceptance = (status: string) => status === 'OPEN' || status === 'REOPENED'

/** Server holati va qabul muhrlaridan kelib chiqadigan ish bosqichi; noma'lum holatda `null`. */
export function ticketStage(ticket: TicketProgress): TicketStage | null {
  if (awaitsAcceptance(ticket.status)) {
    if (!ticket.assignedTo) return 'UNASSIGNED'
    if (ticket.confirmedAt) return 'CONFIRMED'
    if (ticket.receivedAt) return 'RECEIVED'
    return 'ASSIGNED'
  }
  return oneOf(ticket.status, ['IN_PROGRESS', 'RESOLVED', 'CLOSED'] as const)
}

/** Joriy foydalanuvchi mas'ul bo'lsa, uning navbatdagi qadami. */
export function ticketAcceptanceStep(
  ticket: TicketProgress,
  userId: string | null | undefined,
): TicketAcceptanceStep | null {
  if (!userId || ticket.assignedTo !== userId || !awaitsAcceptance(ticket.status)) return null
  if (!ticket.receivedAt) return 'receive'
  if (!ticket.confirmedAt) return 'confirm'
  return null
}

/** Ishni boshlash faqat mas'ul tasdiqlagandan keyin — server `TICKET_NOT_CONFIRMED` bilan rad etadi. */
export function ticketStartBlocked(ticket: TicketProgress): boolean {
  return awaitsAcceptance(ticket.status) && !ticket.confirmedAt
}

/**
 * `PUT /api/crm/tickets/{id}` tanasi — `TicketEdit` (xsib-crm) ning aksi.
 *
 * <h2>⚠️ PUT — TO'LIQ ALMASHTIRISH, QISMAN YANGILASH EMAS</h2>
 * `TicketRepository.updateDetails` bitta `UPDATE` da HAR ustunga
 * qiymat yozadi (`description = :description`, `assigned_to = ...`).
 * Jackson yuborilmagan maydonni `null` qiladi, ya'ni so'rovda tushib
 * qolgan maydon bazada JIMGINA TOZALANADI — xato ham, ogohlantirish
 * ham bo'lmaydi.
 *
 * ⚠️ Shuning uchun bu yerda BIRORTA maydon ixtiyoriy (`?`) EMAS:
 * "bo'sh" ni `null` bilan ATAYLAB aytish kerak, unutib qoldirish
 * bilan emas. Ixtiyoriy maydon TypeScript'ga "yubormasa ham bo'ladi"
 * deb ko'rinardi, serverda esa u tozalash buyrug'i edi.
 *
 * ⚠️ `status` bu yerda YO'Q va bo'lmaydi — sabab {@link ticketTransition}
 * da: har o'tish o'z endpointi bilan boradi va holat mashinasi
 * qoidalariga bo'ysunadi. Formadan holat yuborish o'sha qoidalarni
 * chetlab o'tardi.
 *
 * ⚠️ `subscriberId`/`venueId` ham YO'Q: serverdagi `TicketEdit` ularni
 * umuman qabul qilmaydi (tiketni boshqa mavzuga ko'chirish — tahrir
 * emas, yangi tiket). Ularni bu tipga qo'shish "yubordim, qabul
 * qilinmadi, menga hech kim aytmadi" holatini yaratardi.
 *
 * ⚠️ `dueAt` ham YO'Q va bu tozalash EMAS: server uni `priority` dan
 * QAYTA HISOBLAYDI (`TicketServiceImpl.update`, `created_at` dan).
 */
export interface TicketEdit {
  category: TicketCategory
  priority: TicketPriority
  subject: string
  description: string | null
  assignedTo: string | null
}

export const TICKET_EDIT_FIELDS: readonly (keyof TicketEdit)[] = [
  'category',
  'priority',
  'subject',
  'description',
  'assignedTo',
]

/**
 * ⚠️ TO'LIQ ALMASHTIRISHNING IKKINCHI QATLAMI — ISH VAQTIDA.
 *
 * TypeScript maydon tushib qolishini kompilyatsiyada ushlaydi, lekin
 * faqat tana AYNAN shu tip bilan yozilganda. Tana boshqa joyda
 * yig'ilib, tarqatib (`...`) berilsa tip tekshiruvi ochiq qoladi va
 * yetishmagan maydon serverga "tozala" degan buyruq bo'lib boradi.
 * Bu tekshiruv uni SHU YERDA, tarmoqqa chiqishdan oldin to'xtatadi.
 */
function requireEveryField<T extends object>(
  body: T,
  fields: readonly (keyof T)[],
  what: string,
): void {
  const missing = fields.filter((field) => !(field in body))
  if (missing.length > 0) {
    throw new Error(
      `${what}: PUT to'liq almashtiradi, so'rovda maydon yetishmayapti — ${missing.join(', ')}`,
    )
  }
}

export async function updateTicket(id: string, edit: TicketEdit): Promise<Ticket> {
  requireEveryField(edit, TICKET_EDIT_FIELDS, 'updateTicket')
  const response = await api.put<unknown>(`/api/crm/tickets/${id}`, edit)
  return ticketSchema.parse(response.data)
}

/** Formada ko'rinadigan maydonlar — `TicketEdit` ning tahrirlanadigan qismi */
export interface TicketEditForm {
  category: TicketCategory
  priority: TicketPriority
  subject: string
  description?: string
}

/**
 * Forma qiymatlari + JORIY tiket → `PUT` tanasi.
 *
 * ⚠️ FUNKSIYA va ATAYLAB oynadan tashqarida: qorovul uni CHAQIRIB
 * o'lchaydi (`ticketListParams` bilan bir xil sabab — manba matnida
 * naqsh qidirish "maydon yuborilyaptimi" degan savolga javob bermaydi).
 *
 * ⚠️ `assignedTo` FORMADA YO'Q, lekin so'rovda BOR: mas'ul o'z amali
 * bilan biriktiriladi (`assignTicket`), tahrir esa uni O'ZGARTIRMASDAN
 * qaytarib yuboradi. Usiz har tahrir mas'ulni jimgina bo'shatardi.
 */
export function ticketEditBody(values: TicketEditForm, current: Ticket): TicketEdit {
  return {
    category: values.category,
    priority: values.priority,
    subject: values.subject.trim(),
    description: blankToNull(values.description),
    assignedTo: current.assignedTo ?? null,
  }
}

export type TicketEditBlock = 'closed' | 'unknown'

/**
 * Tahrirlash MUMKINMI va MAYDONLAR nima bilan to'ladi — bitta javobda.
 *
 * ⚠️ Ikki savol ATAYLAB birlashtirilgan. Ajratilsa oyna "tahrirlash
 * mumkin" degan javobdan keyin ham xom `string` ni sanoqli tipga
 * keltirishga majbur bo'lardi va u yerda zaxira qiymat (`?? 'OTHER'`)
 * paydo bo'lardi — ya'ni noma'lum turkum JIMGINA boshqasiga
 * almashardi. Bu `LeadsPage` dagi `?? 'lost'` bilan bir xil oiladan.
 *
 * ⚠️ `closed` — SERVER qoidasi: `TicketServiceImpl.update` yopilgan
 * tiketni `TicketException.NotEditable` bilan rad etadi. Amalni
 * ko'rsatib, keyin xato oynasi chiqarish — panelning boshqa joylarida
 * allaqachon tuzatilgan xato, shuning uchun band O'CHIQ bo'ladi va
 * sababi yorlig'ining O'ZIDA yoziladi.
 *
 * ⚠️ `unknown` — holat/turkum/muhimlik ro'yxatda yo'q. Bu kontrakt
 * ajrashgani degani va bunday yozuvni tahrirlash `PUT` ning to'liq
 * almashtirishi tufayli noma'lum qiymatni YO'QOTARDI.
 */
export type TicketEditPlan =
  | { readonly block: TicketEditBlock }
  | { readonly block: null; readonly values: TicketEditForm }

export function ticketEditPlan(ticket: Ticket): TicketEditPlan {
  const status = oneOf(ticket.status, TICKET_STATUSES)
  const category = oneOf(ticket.category, TICKET_CATEGORIES)
  const priority = oneOf(ticket.priority, TICKET_PRIORITIES)
  if (status === null || category === null || priority === null) {
    return { block: 'unknown' }
  }
  if (TICKET_NEXT[status].length === 0) {
    return { block: 'closed' }
  }
  return {
    block: null,
    values: {
      category,
      priority,
      subject: ticket.subject,
      description: ticket.description ?? '',
    },
  }
}

// --- Lid ----------------------------------------------------------------------

const leadSchema = z.object({
  id: uuidSchema,
  fullName: z.string(),
  /** ⚠️ NIQOBLANGAN — to'liq raqam uchun `revealLeadPhone` */
  phone: z.string().nullish(),
  companyName: z.string().nullish(),
  source: z.string(),
  status: z.string(),
  assignedTo: uuidSchema.nullish(),
  notes: z.string().nullish(),
  lostReason: z.string().nullish(),
  convertedSubscriberId: uuidSchema.nullish(),
  convertedVenueId: uuidSchema.nullish(),
  convertedAt: instantSchema.nullish(),
  lastContactedAt: instantSchema.nullish(),
  anonymizedAt: instantSchema.nullish(),
  createdAt: instantSchema,
  updatedAt: instantSchema.nullish(),
})

export type Lead = z.infer<typeof leadSchema>

export interface LeadFilter {
  search?: string
  status?: LeadStatus
  /** Yopilgan lidlar — sabab va tuzoqlar {@link TicketFilter#includeClosed} da */
  includeClosed?: boolean
  source?: LeadSource
  page?: number
  size?: number
}

export async function fetchLeads(
  filter: LeadFilter = {},
  signal?: AbortSignal,
): Promise<Page<Lead>> {
  const response = await api.get<unknown>('/api/crm/leads', { params: filter, ...(signal ? { signal } : {}) })
  return pageSchema(leadSchema).parse(response.data)
}

export interface LeadDraft {
  fullName: string
  phone: string
  companyName?: string | null
  source: LeadSource
  notes?: string | null
}

export async function createLead(draft: LeadDraft): Promise<Lead> {
  const response = await api.post<unknown>('/api/crm/leads', draft)
  return leadSchema.parse(response.data)
}

/**
 * `PUT /api/crm/leads/{id}` tanasi — serverdagi `LeadDraft` ning aksi.
 *
 * <h2>⚠️ PUT — TO'LIQ ALMASHTIRISH</h2>
 * `LeadResource#update` YARATISH bilan AYNAN BIR XIL DTO ni oladi
 * (`LeadDraft`), `LeadRepository.updateDetails` esa oltala ustunni ham
 * so'zsiz qayta yozadi. Ya'ni so'rovda tushib qolgan maydon bazada
 * JIMGINA `null` bo'ladi — server na xato, na ogohlantirish beradi.
 *
 * ⚠️ Shuning uchun bu tipda ixtiyoriy (`?`) maydon YO'Q — {@link TicketEdit}
 * bilan bir xil sabab. `LeadDraft` (yaratish) esa ixtiyoriy maydonli
 * bo'lib QOLADI: u yerda yuborilmagan maydon bo'sh yozuv yaratadi,
 * mavjud qiymatni o'chirmaydi.
 *
 * ⚠️ `status` YO'Q: lid holati o'z amallari bilan o'zgaradi
 * (`contacted`, `qualify`, `lost`, `convert/*`) va ular holat mashinasi
 * qoidalariga bo'ysunadi. Serverdagi `LeadDraft` da ham holat maydoni
 * umuman yo'q, ya'ni formadan yuborilgan holat JIMGINA e'tiborsiz
 * qolardi.
 */
export interface LeadEdit {
  fullName: string
  phone: string | null
  companyName: string | null
  source: LeadSource
  notes: string | null
  assignedTo: string | null
}

export const LEAD_EDIT_FIELDS: readonly (keyof LeadEdit)[] = [
  'fullName',
  'phone',
  'companyName',
  'source',
  'notes',
  'assignedTo',
]

export async function updateLead(id: string, edit: LeadEdit): Promise<Lead> {
  requireEveryField(edit, LEAD_EDIT_FIELDS, 'updateLead')
  const response = await api.put<unknown>(`/api/crm/leads/${id}`, edit)
  return leadSchema.parse(response.data)
}

export async function assignLead(id: string, assignedTo: string | null): Promise<Lead> {
  const response = await api.post<unknown>(`/api/crm/leads/${id}/assign`, { assignedTo })
  return leadSchema.parse(response.data)
}

/**
 * Mas'ul biriktirishga to'siq — `null` bo'lsa yo'q.
 *
 * ⚠️ SERVER BU YERDA TO'SIQ QO'YMAYDI va bu ATAYLAB CHEKLANGAN.
 * `LeadServiceImpl.assign` holatni umuman tekshirmaydi, ya'ni
 * yopilgan lidga ham mas'ul qo'yish 200 qaytaradi. Lekin bazadagi
 * `lead_touch` trigger'i HAR `UPDATE` da `updated_at` ni yangilaydi,
 * `lead_pii_sweep_idx` esa aynan shu ustun bo'yicha
 * (`WHERE anonymized_at IS NULL AND status IN ('WON','LOST')`)
 * tozalanadigan lidlarni topadi.
 *
 * Ya'ni yopilgan lidga mas'ul qo'yish shaxsiy ma'lumot saqlash
 * muddatini CHO'ZADI — `LeadServiceImpl.update` da terminal holat
 * aynan shu sababdan taqiqlangan. Panel o'sha qoidani `assign`
 * yo'lida ham qo'llaydi: yopilgan lidning mas'ulini almashtirishning
 * amaliy foydasi yo'q, oqibati esa bor.
 *
 * ⚠️ MUROJAATDA bunday cheklov YO'Q va qo'shilmaydi: tiket tomonda
 * saqlash muddati vazifasi umuman yo'q, ya'ni yopilgan tiketga
 * mas'ul qo'yish zararsiz va u kerak bo'ladi (hisobotdagi egalik).
 */
export function leadAssignBlock(lead: Lead): 'anonymized' | 'terminal' | null {
  if (lead.anonymizedAt !== null && lead.anonymizedAt !== undefined) return 'anonymized'
  const status = oneOf(lead.status, LEAD_STATUSES)
  if (status === null || LEAD_NEXT[status].length === 0) return 'terminal'
  return null
}

/** Formada ko'rinadigan maydonlar — `LeadEdit` ning tahrirlanadigan qismi */
export interface LeadEditForm {
  fullName: string
  phone?: string
  companyName?: string
  source: LeadSource
  notes?: string
}

/**
 * Forma qiymatlari + JORIY lid → `PUT` tanasi.
 *
 * ⚠️ FUNKSIYA va oynadan tashqarida — qorovul uni CHAQIRIB o'lchaydi.
 *
 * ⚠️ `assignedTo` formada YO'Q, so'rovda BOR: mas'ul o'z amali bilan
 * biriktiriladi (`assignLead`), tahrir esa uni QAYTARIB yuboradi.
 * Usiz har tahrir mas'ulni jimgina bo'shatardi — va buni sotuvchi
 * faqat lid ro'yxatidan yo'qolganda sezardi.
 */
export function leadEditBody(values: LeadEditForm, current: Lead): LeadEdit {
  return {
    fullName: values.fullName.trim(),
    phone: blankToNull(values.phone),
    companyName: blankToNull(values.companyName),
    source: values.source,
    notes: blankToNull(values.notes),
    assignedTo: current.assignedTo ?? null,
  }
}

export type LeadEditBlock = 'anonymized' | 'terminal' | 'maskedPhone' | 'unknown'

/**
 * Tahrirlash MUMKINMI va MAYDONLAR nima bilan to'ladi — bitta javobda
 * ({@link ticketEditPlan} dagi bilan bir xil sabab).
 *
 * ⚠️ TO'RT to'siq. Uchtasi SERVERDAN o'qilgan:
 *
 * · `anonymized` — `LeadServiceImpl.update` birinchi shuni tekshiradi
 *   (`LeadException.NotEditable`, "saqlash muddati o'tgan");
 * · `terminal` — `if (current.status().isTerminal())`, ya'ni `WON` va
 *   `LOST`. Sabab tekshiruvning O'ZIDA yozilgan: tahrir `updated_at`
 *   ni yangilaydi, `LeadRetentionJob` esa aynan shu ustunga qarab
 *   shaxsiy ma'lumotni tozalaydi — ya'ni yopilgan lidni vaqti-vaqti
 *   bilan "tahrirlash" saqlash muddatini CHEKSIZ cho'zardi;
 * · `unknown` — holat yoki manba ro'yxatda yo'q (kontrakt ajrashgan).
 *
 * ⚠️ To'rtinchisi — `maskedPhone` — PANEL to'sig'i va u eng nozigi.
 * Ro'yxat ham, `GET /{id}` ham telefonni NIQOBLANGAN beradi
 * (`Lead.masked()`). `PUT` esa to'liq almashtiradi, ya'ni:
 *
 *   · niqobni (`+9989****567`) qaytarib yuborish — `Phones.normalize`
 *     yulduzchani tozalamaydi va `@Pattern(E164)` uni RAD etadi (400);
 *   · maydonni umuman yubormaslik — RAQAM O'CHADI, jimgina.
 *
 * Uchinchi yo'l yo'q: to'liq raqamning yagona manbai — `POST /{id}/reveal`
 * (alohida huquq, sabab va audit hodisasi). Shuning uchun raqamli lidni
 * tahrirlash AVVAL ochishni talab qiladi.
 *
 * ⚠️ Raqamsiz lid bu to'siqqa TUSHMAYDI: o'chadigan narsa yo'q.
 *
 * @param fullPhone `revealLeadPhone` bergan to'liq raqam, yoki `undefined`
 */
export type LeadEditPlan =
  | { readonly block: LeadEditBlock }
  | { readonly block: null; readonly values: LeadEditForm }

export function leadEditPlan(lead: Lead, fullPhone: string | undefined): LeadEditPlan {
  if (lead.anonymizedAt !== null && lead.anonymizedAt !== undefined) {
    return { block: 'anonymized' }
  }
  const status = oneOf(lead.status, LEAD_STATUSES)
  const source = oneOf(lead.source, LEAD_SOURCES)
  if (status === null || source === null) {
    return { block: 'unknown' }
  }
  if (LEAD_NEXT[status].length === 0) {
    return { block: 'terminal' }
  }
  const masked = blankToNull(lead.phone)
  if (masked !== null && fullPhone === undefined) {
    return { block: 'maskedPhone' }
  }
  return {
    block: null,
    values: {
      fullName: lead.fullName,
      phone: fullPhone ?? '',
      companyName: lead.companyName ?? '',
      source,
      notes: lead.notes ?? '',
    },
  }
}

/**
 * Tanasiz o'tishlar — `contacted` va `qualify`.
 *
 * ⚠️ `lost` va `WON` bu yerda ATAYLAB YO'Q va bu tuzatilgan nuqsonning
 * o'zagi. Ilgari chaqiruvchi `TRANSITION_PATH[to] ?? 'lost'` yozgan edi,
 * ya'ni xaritada topilmagan HAR QANDAY holat jimgina `/lost` ga
 * aylanardi — va `WON` xaritada yo'q edi. "Yutildi" tugmasi shu tariqa
 * lidni YO'QOTILGAN qilib belgilashga urinardi. Endi bu ikki o'tish
 * o'z funksiyasiga ega (`markLeadLost`, `convertLeadToSubscriber`) va
 * TypeScript "hammasi uchun bitta funksiya" yo'lini umuman bermaydi.
 */
export async function leadTransition(
  id: string,
  to: 'contacted' | 'qualify',
  body?: Record<string, unknown>,
): Promise<Lead> {
  const response = await api.post<unknown>(`/api/crm/leads/${id}/${to}`, body ?? {})
  return leadSchema.parse(response.data)
}

/**
 * Lidni YO'QOTILGAN deb belgilash.
 *
 * ⚠️ `reason` MAJBURIY — backend (`LeadServiceImpl.markLost`) sababsiz
 * so'rovni rad etadi. Ilgari tugma uni umuman so'ramasdi va har bosishda
 * 400 qaytardi; endi sabab modal orqali olinadi va bu yerda tip
 * darajasida majburiy.
 *
 * ⚠️ Sabab shunchaki formallik emas: yo'qotish sabablari sotuv
 * hisobotining yagona manbai ("narx", "raqobatchi", "javob bermadi").
 */
export async function markLeadLost(id: string, reason: string): Promise<Lead> {
  const response = await api.post<unknown>(`/api/crm/leads/${id}/lost`, { reason })
  return leadSchema.parse(response.data)
}

/**
 * Lidni YUTILGAN deb belgilash — MAVJUD abonentga bog'lash orqali.
 *
 * ⚠️ Backend `subscriberId` ni TEKSHIRADI (`SubscriberDirectory`) va
 * topilmasa 422 qaytaradi. Ya'ni "yangi mijoz" oqimida avval abonent
 * YARATILISHI, keyin shu chaqiruv qilinishi kerak — tartibni teskari
 * qilib bo'lmaydi.
 */
export async function convertLeadToSubscriber(id: string, subscriberId: string): Promise<Lead> {
  const response = await api.post<unknown>(`/api/crm/leads/${id}/convert/subscriber`, {
    subscriberId,
  })
  return leadSchema.parse(response.data)
}

/**
 * Lidni YUTILGAN deb belgilash — MAVJUD ob'ektga bog'lash orqali.
 *
 * ⚠️ Bu `convertLeadToSubscriber` ning "yana bir varianti" EMAS, ikkinchi
 * NISHON TURI. Baza ikkalasini bir vaqtda saqlashga yo'l qo'ymaydi:
 *
 *     CONSTRAINT lead_won_consistent CHECK (
 *         status = 'WON' AND converted_at IS NOT NULL
 *         AND (converted_subscriber_id IS NOT NULL)
 *          <> (converted_venue_id IS NOT NULL))
 *
 * Ya'ni "shunchaki yutildi" holati sxema darajasida MAVJUD EMAS —
 * yutilgan lid aynan BITTA nishonga bog'langan bo'lishi shart. Shuning
 * uchun panelda ham nishon TANLANADI, sukut qiymat bilan tanlanmaydi.
 *
 * ⚠️ Backend `venueId` ni IKKI daraja bo'yicha tekshiradi:
 *
 *   1. DOIRA — `LeadResource#requireVenueTargetInScope`, yetmasa
 *      **403** (`404` emas: doira qorovuli ishlaganda `xsib-crm`
 *      ob'ekt bor-yo'qligini hali bilmaydi);
 *   2. MAVJUDLIK — `VenueDirectory` orqali `xsib-venue` dan
 *      so'raladi, topilmasa **422** (B14, 2026-08-15). Bu abonent
 *      yo'lidagi bilan AYNAN bir xil kod, ya'ni panel ikki nishon
 *      turini bir xil ishlaydi.
 *
 * ⚠️ `xsib-venue` javob bermasa **502/504** keladi va lid
 * `QUALIFIED` bo'lib qoladi — konvertatsiya JIMGINA o'tkazib
 * yuborilmaydi (`RestVenueDirectory` javadoc'ida uch sabab).
 *
 * ⚠️ Ob'ektning O'ZI bu yerda YARATILMAYDI — sabab `LeadWonVenueModal`
 * javadoc'ida.
 */
export async function convertLeadToVenue(id: string, venueId: string): Promise<Lead> {
  const response = await api.post<unknown>(`/api/crm/leads/${id}/convert/venue`, { venueId })
  return leadSchema.parse(response.data)
}

/**
 * To'liq telefon raqamini ochadi.
 *
 * ⚠️ XAVFLI-GA YAQIN AMAL: alohida huquq (`crm.lead.reveal`), audit
 * hodisasi va SABAB talab qilinadi. Natija React Query keshiga
 * SAQLANMAYDI — chaqiruvchi uni faqat lokal holatda tutadi va sahifa
 * yangilanganda yo'qoladi (vaucher kodlari bilan bir xil qoida).
 */
export async function revealLeadPhone(id: string, reason: string): Promise<Lead> {
  const response = await api.post<unknown>(`/api/crm/leads/${id}/reveal`, { reason })
  return leadSchema.parse(response.data)
}

// --- Muloqot ------------------------------------------------------------------

const interactionSchema = z.object({
  id: uuidSchema,
  ticketId: uuidSchema.nullish(),
  leadId: uuidSchema.nullish(),
  subscriberId: uuidSchema.nullish(),
  subscriberLogin: z.string().nullish(),
  venueId: uuidSchema.nullish(),
  venueName: z.string().nullish(),
  channel: z.string(),
  direction: z.string(),
  subject: z.string().nullish(),
  notes: z.string().nullish(),
  outcome: z.string().nullish(),
  durationSeconds: z.number().nullish(),
  staffId: uuidSchema.nullish(),
  occurredAt: instantSchema,
  createdAt: instantSchema.nullish(),
  /**
   * ⚠️ Tuzatilgan bo'lsa to'ladi — jadval «tahrirlangan» belgisini
   * AYNAN shundan oladi. Tuzatishni yashirish jurnalga ishonchni
   * yo'qotardi.
   */
  editedAt: instantSchema.nullish(),
  /**
   * ⚠️ Odatiy ro'yxatda HECH QACHON to'lmaydi — server bekor
   * qilinganlarni umuman qaytarmaydi. Faqat bekor qilish amalining
   * O'Z javobida keladi.
   */
  voidedAt: instantSchema.nullish(),
})

export type Interaction = z.infer<typeof interactionSchema>

/**
 * ⚠️ Bu yerda `search` YO'Q va bu taxmin emas: `InteractionQuery`
 * (xsib-crm) matn qidiruvi maydonini umuman saqlamaydi — server
 * faqat bog'lanish va sana oynasi bo'yicha filtrlaydi. Bo'lmagan
 * parametrni yuborish jimgina e'tiborsiz qolardi va panel «qidiruv
 * ishlamayapti» degan taassurot berardi.
 *
 * ⚠️ `venueId`, `from`, `to` — SERVER QABUL QILADI
 * (`InteractionResource#list`), lekin bu tipda yo'q edi, ya'ni panel
 * mavjud filtrlardan uchtasini umuman ishlatmasdi.
 */
export interface InteractionFilter {
  ticketId?: string
  leadId?: string
  subscriberId?: string
  venueId?: string
  /** ISO-8601 (`Instant`) — chegarasiz bo'lsa berilmaydi */
  from?: string
  to?: string
  page?: number
  size?: number
}

export async function fetchInteractions(
  filter: InteractionFilter = {},
  signal?: AbortSignal,
): Promise<Page<Interaction>> {
  const response = await api.get<unknown>('/api/crm/interactions', {
    params: filter,
    ...(signal ? { signal } : {}),
  })
  return pageSchema(interactionSchema).parse(response.data)
}

export interface InteractionDraft {
  ticketId?: string | null
  leadId?: string | null
  subscriberId?: string | null
  /**
   * ⚠️ Bu maydon tipda YO'Q edi, backend `InteractionDraft` da esa BOR
   * va forma uni allaqachon yuborardi. Ya'ni tip kontraktdan orqada
   * qolgan edi: TypeScript "ob'ektga bog'lash" yo'lini umuman
   * ko'rmasdi va uni tekshira ham olmasdi.
   */
  venueId?: string | null
  channel: InteractionChannel
  direction: InteractionDirection
  subject?: string | null
  notes?: string | null
  outcome?: InteractionOutcome | null
  durationSeconds?: number | null
  occurredAt?: string | null
}

export async function createInteraction(draft: InteractionDraft): Promise<Interaction> {
  const response = await api.post<unknown>('/api/crm/interactions', draft)
  return interactionSchema.parse(response.data)
}

/**
 * Tuzatiladigan maydonlar — operatorning O'Z bayoni.
 *
 * ⚠️ `channel`, `direction`, `occurredAt` va bog'lanishlar bu yerda
 * ATAYLAB yo'q: ular hodisaning O'ZI va serverda ham qabul
 * qilinmaydi (`InteractionEdit`). Ularni bu tipga qo'shish "yubordim,
 * qabul qilinmadi, menga hech kim aytmadi" holatini yaratardi.
 */
export interface InteractionEdit {
  subject?: string | null
  notes?: string | null
  outcome?: InteractionOutcome | null
  durationSeconds?: number | null
}

export async function updateInteraction(id: string, edit: InteractionEdit): Promise<Interaction> {
  const response = await api.put<unknown>(`/api/crm/interactions/${id}`, edit)
  return interactionSchema.parse(response.data)
}

/**
 * Yozuvni ro'yxatdan chiqarish.
 *
 * ⚠️ Yo'l `POST /{id}/void`, `DELETE /{id}` EMAS — sabab MAJBURIY
 * (ya'ni tana kerak) va qator aslida O'CHMAYDI. Batafsil —
 * `InteractionResource#voidEntry`.
 */
export async function voidInteraction(id: string, reason: string): Promise<Interaction> {
  const response = await api.post<unknown>(`/api/crm/interactions/${id}/void`, { reason })
  return interactionSchema.parse(response.data)
}

// --- `DataTable` uchun sahifa oluvchilar ---------------------------------------
//
// ⚠️ SAHIFALASH, QIDIRUV VA FILTR — SERVERDA. Uchala CRM ro'yxati ham
// `page`/`size` ni HAQIQATDAN qabul qiladi (`LeadQuery`, `TicketQuery`,
// `InteractionQuery` — hammasida `MAX_SIZE = 200`), shuning uchun bu
// yerda butun ro'yxatni tortib mijozda kesish YO'Q.
//
// ⚠️ SERVER SAHIFASI NOLDAN, jadval esa BIRDAN sanaydi. Farq FAQAT shu
// yerda tarjima qilinadi (`state.page - 1`) — sahifalarda takror
// yozilganda bittasi unutilib, 1- va 2-sahifa bir xil qatorni ko'rsatardi.
//
// ⚠️ USTUNLARDA `sorter` YO'Q va bu ataylab: uchala endpoint ham saralash
// parametrini QABUL QILMAYDI. Tartib serverda QOTIRILGAN va u allaqachon
// «oxirgi qo'shilgan birinchi»:
//   · lidlar     — `ORDER BY created_at DESC`  (`LeadRepository`)
//   · murojaatlar— `ORDER BY opened_at DESC`   (`TicketRepository`)
//   · muloqotlar — `ORDER BY occurred_at DESC` (`InteractionRepository`)
// Ustunga `sorter: true` qo'yish faqat JORIY SAHIFANI saralardi.

/**
 * URL holatidagi filtr qiymati DOIM massiv, server esa YAKKA qiymat
 * kutadi. Ro'yxatga kirmagan qiymat (havolada qo'lda o'zgartirilgan)
 * TASHLANADI — aks holda server 400 qaytarardi va sabab ko'rinmasdi.
 */
function pickOne<T extends string>(
  values: readonly string[] | undefined,
  allowed: readonly T[],
): T | undefined {
  const first = values?.[0]
  if (first === undefined) return undefined
  return (allowed as readonly string[]).includes(first) ? (first as T) : undefined
}

/** `f.<ustun>=yes` shaklidagi bayroq */
function pickFlag(values: readonly string[] | undefined): boolean {
  return values?.[0] === 'yes'
}

function optional<K extends string, V>(key: K, value: V | undefined): Record<K, V> | Record<string, never> {
  return value === undefined ? {} : ({ [key]: value } as Record<K, V>)
}

/**
 * «Yopiq» ning ta'rifi — HAR ro'yxatda BOSHQA va u SERVERDAN olingan
 * (`LeadStatus.isClosed()`, `TicketStatus.isClosed()`).
 *
 * ⚠️ Lidda bu FAQAT `LOST`. `WON` ro'yxatda QOLADI (egasi qarori
 * 2026-08-21): sotuvchi yutuqlarini va konversiyani aynan shu
 * ro'yxatdan o'qiydi, yashirilsa ro'yxat «kim yutdi» degan savolga
 * javob bera olmasdi.
 *
 * ⚠️ Murojaatda bu FAQAT `CLOSED`. `RESOLVED` — hali qayta ochilishi
 * mumkin bo'lgan holat (`TICKET_NEXT`), ya'ni tugagan ish emas.
 */
export const LEAD_CLOSED_STATUS: LeadStatus = 'LOST'
export const TICKET_CLOSED_STATUS: TicketStatus = 'CLOSED'

/**
 * So'rov parametrlari — FUNKSIYA, chunki qorovul uni CHAQIRIB o'lchaydi.
 *
 * ⚠️ `fetchTicketPage` ichidan ataylab ajratilgan: manba matnida naqsh
 * qidirish sukut qiymatni ko'rmasdi. Naqsh o'tadigan, lekin xulqi
 * teskari kod yozish oson — sabab `tests/closed-filter.mjs` sarlavhasida.
 */
export function ticketListParams(state: TableState): TicketFilter {
  const search = state.search.trim()
  return {
    page: state.page - 1,
    size: state.pageSize,
    ...optional('search', search || undefined),
    ...optional('status', pickOne(state.filters['status'], TICKET_STATUSES)),
    ...optional('category', pickOne(state.filters['category'], TICKET_CATEGORIES)),
    ...optional('priority', pickOne(state.filters['priority'], TICKET_PRIORITIES)),
    // ⚠️ Bayroq FAQAT yoqilganda yuboriladi: `onlyOverdue=false` server
    // uchun sukut qiymat va uni har so'rovda tashish URL'ni ham,
    // kesh kalitini ham shovqinga to'ldirardi.
    ...(pickFlag(state.filters['overdue']) ? { onlyOverdue: true } : {}),
    ...(pickFlag(state.filters['ackOverdue']) ? { onlyAcknowledgeOverdue: true } : {}),
    // ⚠️ `includeClosed` esa TESKARISI — u DOIM yuboriladi, chunki
    // serverdagi sukut qiymat `true` va yuborilmagani «hammasi» degani.
    includeClosed: showsClosed(state.filters['status']?.[0]),
  }
}

export function fetchTicketPage(
  state: TableState,
  signal: AbortSignal,
): Promise<PageResult<Ticket>> {
  return fetchTickets(ticketListParams(state), signal).then((page) => ({
    items: page.rows,
    total: page.total,
  }))
}

/**
 * Sukut ko'rinish NECHTA murojaatni yashirganini sanaydi.
 *
 * ⚠️ Holat AYNAN yopilganga qo'yiladi va shu bilan birga
 * `includeClosed: false` ham yuboriladi — server bu juftlikda bayroqni
 * e'tiborsiz qoldiradi va yopilganlarni ROSTDAN qaytaradi. Zanjirning
 * eng qimmat bo'g'ini shu: buzilsa nishon doim «0» ko'rsatardi va
 * yashiringan qatorlar jimgina yo'q bo'lardi.
 *
 * ⚠️ Sahifa so'ralmaydi (`size: 1`) — javobdan faqat `total` kerak.
 */
export function ticketClosedCountParams(state: TableState): TicketFilter {
  return {
    ...ticketListParams(state),
    page: 0,
    size: 1,
    status: TICKET_CLOSED_STATUS,
  }
}

export function countClosedTickets(state: TableState, signal: AbortSignal): Promise<number> {
  return fetchTickets(ticketClosedCountParams(state), signal).then((page) => page.total)
}

export function leadListParams(state: TableState): LeadFilter {
  const search = state.search.trim()
  return {
    page: state.page - 1,
    size: state.pageSize,
    ...optional('search', search || undefined),
    ...optional('status', pickOne(state.filters['status'], LEAD_STATUSES)),
    ...optional('source', pickOne(state.filters['source'], LEAD_SOURCES)),
    includeClosed: showsClosed(state.filters['status']?.[0]),
  }
}

export function fetchLeadPage(state: TableState, signal: AbortSignal): Promise<PageResult<Lead>> {
  return fetchLeads(leadListParams(state), signal).then((page) => ({
    items: page.rows,
    total: page.total,
  }))
}

/** Yashiringan lidlar soni — sabab {@link ticketClosedCountParams} da */
export function leadClosedCountParams(state: TableState): LeadFilter {
  return {
    ...leadListParams(state),
    page: 0,
    size: 1,
    status: LEAD_CLOSED_STATUS,
  }
}

export function countClosedLeads(state: TableState, signal: AbortSignal): Promise<number> {
  return fetchLeads(leadClosedCountParams(state), signal).then((page) => page.total)
}

/**
 * ⚠️ QIDIRUV MAYDONI YO'Q va u qo'shilmaydi: `InteractionQuery` da matn
 * qidiruvi umuman yo'q (yuqoridagi `InteractionFilter` izohi). Sahifa
 * `searchable` ni yoqmaydi — ishlamaydigan maydonni chizish
 * «qidiruv buzuq» degan xulosaga olib borardi.
 */
export function fetchInteractionPage(
  state: TableState,
  signal: AbortSignal,
): Promise<PageResult<Interaction>> {
  return fetchInteractions(
    {
      page: state.page - 1,
      size: state.pageSize,
      ...optional('venueId', state.filters['venue']?.[0]),
      ...optional('from', state.filters['from']?.[0]),
      ...optional('to', state.filters['to']?.[0]),
    },
    signal,
  ).then((page) => ({ items: page.rows, total: page.total }))
}
